import React, { useEffect, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { getDataColor } from '../../../styles/dataColors.js';

const BarChartMulti = ({
  data = [],
  height = 300,
  mobileHeight = null,
  xKey = 'name',
  series = [
    { dataKey: 'tareas', name: 'Total Tareas', color: getDataColor(0) },
    { dataKey: 'completadas', name: 'Completadas', color: getDataColor(1) }
  ],
  yAllowDecimals = false,
  showLegend = true,
  legendIconType = 'circle',
  legendFontSize = 12,
  showGrid = true,
  showTooltip = true,
  gridStroke = 'rgb(var(--app-border-rgb))',
  axisStroke = 'rgb(var(--app-text-muted-rgb))',
  axisFontSize = 12,
  xAxisHeight = 50,
  xAxisInterval = 0,
  xTickWrap = true,
  xTickMaxChars = 12,
  xTickMaxLines = 2,
  xTickLineHeight = 12,
  xTickDy = 12,
  xTickMobileAngle = null,
  xTickMobileHeight = null,
  xTickMobileMaxChars = null,
  emptyMessage = '',
  emptyYAxisTicks = [0, 1, 2, 3, 4],
  marginTop = 0,
  marginRight = 0,
  marginBottom = 0,
  marginLeft = 0,
  cursorFill = 'transparent',
  className = '',
  barSize = null,
  barCategoryGap = '20%'
}) => {
  const [isMobile, setIsMobile] = useState(() => (
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
  ));

  useEffect(() => {
    if (xTickMobileAngle === null || typeof window === 'undefined') return undefined;

    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const updateIsMobile = () => setIsMobile(mediaQuery.matches);

    updateIsMobile();
    mediaQuery.addEventListener('change', updateIsMobile);
    return () => mediaQuery.removeEventListener('change', updateIsMobile);
  }, [xTickMobileAngle]);

  const hasData = data.some((row) => series.some((item) => Number(row?.[item.dataKey]) > 0));
  const showEmptyState = Boolean(emptyMessage) && !hasData;
  const useMobileTickOrientation = isMobile && xTickMobileAngle !== null && !showEmptyState;
  const chartData = showEmptyState
    ? [{ [xKey]: '', ...series.reduce((values, item) => ({ ...values, [item.dataKey]: 0 }), {}) }]
    : data;
  const resolvedXAxisHeight = useMobileTickOrientation && xTickMobileHeight
    ? xTickMobileHeight
    : xAxisHeight;
  const resolvedHeight = isMobile && mobileHeight ? mobileHeight : height;

  const wrapLabel = (value) => {
    if (!xTickWrap) return [String(value ?? '')];
    const text = String(value ?? '');
    if (!text) return [''];

    // Si el texto completo cabe en una línea, devolverlo sin dividir
    if (text.length <= xTickMaxChars) {
      return [text];
    }

    const words = text.split(' ');
    const lines = [];
    let current = '';

    words.forEach((word) => {
      const next = current ? `${current} ${word}` : word;
      if (next.length <= xTickMaxChars) {
        current = next;
        return;
      }

      if (current) {
        lines.push(current);
        current = '';
      }

      if (word.length > xTickMaxChars) {
        const chunks = word.match(new RegExp(`.{1,${xTickMaxChars}}`, 'g')) || [];
        chunks.forEach((chunk) => lines.push(chunk));
      } else {
        current = word;
      }
    });

    if (current) lines.push(current);

    if (lines.length <= xTickMaxLines) return lines;
    const trimmed = lines.slice(0, xTickMaxLines);
    const lastIndex = trimmed.length - 1;
    trimmed[lastIndex] = `${trimmed[lastIndex]}…`;
    return trimmed;
  };

  const renderTick = (props) => {
    const { x, y, payload } = props;

    if (useMobileTickOrientation) {
      const fullLabel = String(payload?.value ?? '');
      const maxChars = xTickMobileMaxChars || xTickMaxChars;
      const label = fullLabel.length > maxChars
        ? `${fullLabel.slice(0, Math.max(maxChars - 1, 1))}…`
        : fullLabel;

      return (
        <g transform={`translate(${x},${y})`}>
          <text
            textAnchor="end"
            fill={axisStroke}
            fontSize={axisFontSize}
            dy={xTickDy + 8}
            transform={`rotate(${xTickMobileAngle})`}
          >
            {label}
          </text>
        </g>
      );
    }

    const lines = wrapLabel(payload?.value);

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          textAnchor="middle"
          fill={axisStroke}
          fontSize={axisFontSize}
          dy={0}
        >
          {lines.map((line, index) => (
            <tspan x="0" dy={index === 0 ? xTickDy + 8 : xTickLineHeight} key={`${line}-${index}`}>
              {line}
            </tspan>
          ))}
        </text>
      </g>
    );
  };

  return (
    <div className={`relative ${className}`.trim()}>
      <ResponsiveContainer width="100%" height={resolvedHeight}>
        <BarChart
          data={chartData}
          margin={{ top: marginTop, right: marginRight, bottom: marginBottom, left: marginLeft }}
          barCategoryGap={barCategoryGap}
        >
          {showGrid ? <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} /> : null}
          <XAxis
            dataKey={xKey}
            stroke={axisStroke}
            fontSize={axisFontSize}
            height={resolvedXAxisHeight}
            interval={xAxisInterval}
            tick={renderTick}
          />
          <YAxis
            stroke={axisStroke}
            allowDecimals={yAllowDecimals}
            domain={showEmptyState ? [0, Math.max(...emptyYAxisTicks)] : undefined}
            ticks={showEmptyState ? emptyYAxisTicks : undefined}
          />
          {showTooltip && !showEmptyState ? (
            <Tooltip
              cursor={{ fill: cursorFill }}
              contentStyle={{
                backgroundColor: 'rgb(var(--app-surface-2-rgb))',
                border: '1px solid rgb(var(--app-border-rgb))',
                borderRadius: 8,
                color: 'rgb(var(--app-text-rgb))'
              }}
              labelStyle={{ color: 'rgb(var(--app-text-rgb))' }}
              itemStyle={{ color: 'rgb(var(--app-text-rgb))' }}
            />
          ) : null}
          {showLegend ? (
            <Legend
              iconType={legendIconType}
              wrapperStyle={{ fontSize: legendFontSize, color: 'rgb(var(--app-text-muted-rgb))' }}
            />
          ) : null}
          {series.map((item, index) => (
            <Bar
              key={`${item.dataKey}-${index}`}
              dataKey={item.dataKey}
              fill={item.color}
              name={item.name}
              barSize={barSize}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
      {showEmptyState && (
        <div className="pointer-events-none absolute inset-x-0 top-1/2 flex -translate-y-1/2 justify-center px-12 text-center">
          <span className="rounded-md bg-surface/85 px-3 py-2 text-sm font-medium text-content-muted">
            {emptyMessage}
          </span>
        </div>
      )}
    </div>
  );
};

export default BarChartMulti;
